/**
 * Costs & Margin Hub tab — guided questions + sticky margin strip.
 */
import '../../../components/docs/HubDocRowActions.css'
import './costsMargin/costsMargin.css'
import MarginStrip from './costsMargin/MarginStrip'
import GuidedQuestionCard from './costsMargin/GuidedQuestionCard'
import AnsweredList from './costsMargin/AnsweredList'
import CostsTable from './costsMargin/CostsTable'
import SubCosSection from './costsMargin/SubCosSection'
import DeleteConfirmDialog from './costsMargin/DeleteConfirmDialog'
import EditCostDialog from './costsMargin/EditCostDialog'
import { useCostsMarginData } from './costsMargin/useCostsMarginData'
import { isOverheadCostDescription } from '../../../lib/costsMarginOverhead'
import { formatMoneyInputBlur } from '../../../lib/formatters'

interface Props {
  projectId: number
  onOpenChangeOrders?: () => void
}

export default function CostsMarginTab({ projectId, onOpenChangeOrders }: Props) {
  const d = useCostsMarginData(projectId)
  const q = d.questions[Math.min(d.currentIndex, Math.max(0, d.questions.length - 1))]
  const nonLaborCosts = d.costs.filter((c) => c.category !== 'labor')

  function skip() {
    if (!q) return
    d.setSkippedIds((ids) => (ids.includes(q.id) ? ids : [...ids, q.id]))
    d.setCurrentIndex((i) => Math.min(i + 1, d.questions.length - 1))
    d.showToast('Question skipped — marked not answered')
  }

  function jumpIndex(idx: number) {
    if (idx < 0 || idx >= d.questions.length) return
    const id = d.questions[idx]?.id
    if (id) d.setSkippedIds((ids) => ids.filter((x) => x !== id))
    d.setCurrentIndex(idx)
  }

  function prefillFor(question = q) {
    if (!question) return undefined
    if (question.kind === 'subcontract_amount' && question.projectVendorId != null) {
      const row = d.margin?.answered_state?.subcontract_amounts?.find(
        (s) => s.project_vendor_id === question.projectVendorId,
      )
      if (row && (row.subcontract_amount_cents || 0) > 0) {
        return { amount: formatMoneyInputBlur(String(row.subcontract_amount_cents / 100)) }
      }
    }
    if (question.kind === 'labor') {
      const labor = d.costs.filter((c) => c.category === 'labor')
      const cents = labor.reduce((s, c) => s + c.amount_cents, 0)
      if (cents > 0) return { amount: formatMoneyInputBlur(String(cents / 100)), text: labor[0]?.description || 'Own labor' }
    }
    if (question.kind === 'overhead') {
      const oh = d.costs.filter((c) => isOverheadCostDescription(c.description))
      const cents = oh.reduce((s, c) => s + c.amount_cents, 0)
      if (cents > 0) {
        return {
          amount: formatMoneyInputBlur(String(cents / 100)),
          text: oh[0]?.description || 'Overhead / general conditions',
        }
      }
    }
    return undefined
  }

  async function saveAndMaybeAdvance(payload: Parameters<typeof d.saveMut.mutateAsync>[0]) {
    await d.saveMut.mutateAsync(payload)
    const advance =
      payload.kind === 'subcontract_amount'
      || (payload.kind === 'job_cost'
        && (payload.category === 'labor' || isOverheadCostDescription(payload.description)))
    if (advance) {
      d.setCurrentIndex((i) => Math.min(i + 1, d.questions.length - 1))
    }
  }

  /** Inline Answered-list save — updates strip in place; never scrolls / jumps guided card. */
  async function saveInline(payload: Parameters<typeof d.saveMut.mutateAsync>[0]) {
    await d.saveMut.mutateAsync(payload)
  }

  return (
    <div className="cm-layout" data-testid="costs-margin-tab">
      <div className="cm-column">
        {q && (
          <GuidedQuestionCard
            question={q}
            index={d.currentIndex}
            total={d.questions.length}
            margin={d.margin}
            savedFlash={d.savedFlash}
            busy={d.saveMut.isPending}
            prefill={prefillFor(q)}
            onSkip={skip}
            onNext={() => {
              if (d.currentIndex >= d.questions.length - 1) {
                d.showToast('Add more other costs anytime — or finish when ready')
                return
              }
              d.setCurrentIndex((i) => Math.min(i + 1, d.questions.length - 1))
            }}
            onJumpIndex={jumpIndex}
            onSave={saveAndMaybeAdvance}
            onOpenChangeOrders={onOpenChangeOrders}
          />
        )}
        <div className="cm-card">
          <AnsweredList
            questions={d.questions}
            margin={d.margin}
            costs={d.costs}
            answered={d.margin?.answered_state}
            skippedIds={d.skippedIds}
            busy={d.saveMut.isPending}
            onSave={saveInline}
          />
          <SubCosSection
            changeOrders={d.margin?.change_orders?.all ?? []}
            unmatched={d.margin?.unmatched_sub_cos ?? []}
            vendorNames={d.vendorNames}
          />
          <CostsTable
            costs={nonLaborCosts}
            onEdit={(row) => d.setEditCost(row)}
            onDelete={(row) => d.setDeleteTarget(row)}
          />
        </div>
        <div className="cm-exit-bar">
          <button
            type="button"
            className="cm-btn cm-btn-dark"
            onClick={() => {
              if (d.currentIndex >= d.questions.length - 1) {
                d.showToast('Add more other costs anytime — or finish when ready')
                return
              }
              d.setCurrentIndex((i) => Math.min(i + 1, d.questions.length - 1))
            }}
            data-testid="cm-next-question"
          >
            Next question
          </button>
          <button type="button" className="cm-btn" onClick={() => d.showToast('Closeout report opens when available')}>
            Finish and open the closeout report
          </button>
          <span className="cm-resume">Leave anytime.<br />Your place and answers are saved.</span>
        </div>
      </div>
      <aside className="cm-column"><MarginStrip data={d.margin} costs={d.costs} /></aside>
      <DeleteConfirmDialog
        open={!!d.deleteTarget}
        itemName={d.deleteTarget?.description ?? ''}
        onCancel={() => d.setDeleteTarget(null)}
        onConfirm={() => { if (d.deleteTarget) d.deleteMut.mutate(d.deleteTarget.id) }}
      />
      {d.editCost && (
        <EditCostDialog
          row={d.editCost}
          onClose={() => d.setEditCost(null)}
          onSave={async (patch) => {
            await d.updateJobCost(d.editCost!.id, patch)
            d.flashSaved(); d.showToast('Cost updated'); d.setEditCost(null); await d.invalidate()
          }}
        />
      )}
      {d.Toast}
    </div>
  )
}
